import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { FileSignature, ArrowLeft } from 'lucide-react';
import { Button } from '../../components/ui/Button';
import { PageHeader } from '../../components/shared/PageHeader';
import { LoadingState } from '../../components/shared/LoadingState';
import { ErrorState } from '../../components/shared/ErrorState';
import { PDFPreview } from '../documents/components/PDFPreview';
import { SignatureModal } from './components/SignatureModal';
import { useSignatureEnvelopes } from '../../core/hooks/useSignatureEnvelopes';
import { useEnvelopes } from '../../core/hooks/useEnvelopes';
import { useAuth } from '../../core/contexts/AuthContext';
import { Card, CardContent } from '../../components/ui/Card';
import { SignatureEnvelopeStatus } from '../../core/models/SignatureEnvelope';
import { logError } from '../../lib/utils';
import { useToast } from '../../core/hooks/useToast';

export const CompanySigningPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const { getEnvelope, submitCompanySignature, loading } = useSignatureEnvelopes();
  const { envelopes } = useEnvelopes(null);

  const [envelope, setEnvelope] = useState<any>(null);
  const [envelopeLoading, setEnvelopeLoading] = useState(true);
  const [signatureModalOpen, setSignatureModalOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const toast = useToast();

  useEffect(() => {
    if (!id) return;

    const fetchEnvelope = async () => {
      setEnvelopeLoading(true);
      try {
        const found = envelopes.find(e => e.envelopeId === id);
        if (found) {
          setEnvelope(found);
        } else {
          const fetched = await getEnvelope(id);
          setEnvelope(fetched);
        }
      } catch (err) {
        logError('signatures', err, { source: 'CompanySigningPage.fetchEnvelope', envelopeId: id });
      } finally {
        setEnvelopeLoading(false);
      }
    };

    fetchEnvelope();
  }, [id, envelopes, getEnvelope]);

  const handleSubmitSignature = async (data: {
    signatureType: 'draw' | 'type';
    signatureImage?: string;
    typedName?: string;
    signingDate: string;
    consentGiven: boolean;
  }) => {
    if (!id) return;

    setSubmitting(true);
    try {
      await submitCompanySignature(
        id,
        data.signatureType,
        data.signatureImage,
        data.typedName,
        data.signingDate
      );

      toast.success(
        'Envelope completed',
        'The final signed PDF has been generated.'
      );
      navigate(`/envelopes/${id}`);
    } catch (err: any) {
      logError('signatures', err, { source: 'CompanySigningPage.handleSubmitSignature', envelopeId: id });
      toast.error(
        "Your signature wasn't submitted",
        err.message ||
          'Nothing was saved. Check your connection and try signing again.'
      );
    } finally {
      setSubmitting(false);
    }
  };

  if (envelopeLoading || loading) {
    return <LoadingState message="Loading envelope..." />;
  }

  if (!envelope) {
    return <ErrorState message="Envelope not found" />;
  }

  // Verify status
  if (envelope.status !== SignatureEnvelopeStatus.CLIENT_SIGNED) {
    return (
      <div className="space-y-6">
        <PageHeader
          title="Cannot Sign"
          description="The project signer must sign before the company can sign"
          icon={FileSignature}
          actions={
            <Button variant="outline" onClick={() => navigate(`/envelopes/${id}`)}>
              <ArrowLeft className="mr-2 h-4 w-4" />
              Back to Envelope
            </Button>
          }
        />
        <Card>
          <CardContent className="pt-6">
            <p className="text-gray-600">
              This envelope is currently in "{envelope.status}" status. The project signer must sign first.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Verify user is the company signer
  if (envelope.companySigner.userId !== currentUser?.uid) {
    return (
      <div className="space-y-6">
        <PageHeader
          title="Access Denied"
          description="You are not the designated company signer for this envelope"
          icon={FileSignature}
          actions={
            <Button variant="outline" onClick={() => navigate(`/envelopes/${id}`)}>
              <ArrowLeft className="mr-2 h-4 w-4" />
              Back to Envelope
            </Button>
          }
        />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title="Sign as Company"
        description="Step 2 of 2: Company Signing"
        icon={FileSignature}
        actions={
          <Button variant="outline" onClick={() => navigate(`/envelopes/${id}`)}>
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back
          </Button>
        }
      />

      <Card>
        <CardContent className="pt-6">
          <div className="space-y-4">
            <div>
              <h2 className="font-semibold mb-2">Document for Review</h2>
              <p className="text-sm text-gray-600 mb-4">
                The project signer has already signed. Please review the document and add your signature below.
              </p>
            </div>

            {/* PDF Viewer */}
            <div className="border rounded-lg overflow-hidden">
              <PDFPreview
                file={envelope.originalPdfUrl}
                showDownload={false}
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Signing Action */}
      <div className="flex justify-center">
        <Button
          onClick={() => setSignatureModalOpen(true)}
          size="lg"
          className="w-full md:w-auto"
        >
          <FileSignature className="mr-2 h-5 w-5" />
          Sign as Company
        </Button>
      </div>

      {/* Signature Modal */}
      <SignatureModal
        open={signatureModalOpen}
        onClose={() => setSignatureModalOpen(false)}
        onSubmit={handleSubmitSignature}
        showOtp={false}
        loading={submitting}
        stepLabel="Step 2 of 2: Company Signing"
      />
    </div>
  );
};
